import { useEffect, useMemo, useRef, useState, type FormEvent } from 'react';
import {
  Banner,
  BarRow,
  Card,
  ChecklistRow,
  Chip,
  Empty,
  PageHeader,
  SectionHead,
  Segmented,
  StagePill,
  Stat,
  ThemeSwitch,
} from '../components/ui';
import { UserAvatar } from '../components/UserAvatar';
import { PARTNERSHIP_EVIDENCE } from '../lib/evidence';
import {
  DEFAULT_SPRINT_START,
  DEFAULT_TOTAL_DAYS,
  daysUntilStart,
  formatSprintLabel,
  nextMondayIso,
  sprintDayIndex,
  sprintEndFromStart,
} from '../lib/sprint';
import {
  api,
  loadSession,
  patchSession,
  type MemberRole,
  type OrgMember,
  type ProfileMe,
} from '../state/api';
import { useStore } from '../state/store';
import type { AnchorState, Partner, ThemeMode } from '../types/anchor';

type Props = {
  active: string;
  state: AnchorState;
  partners: Partner[];
  onAdd: () => void;
  onSelect: (p: Partner) => void;
  onNavigate: (name: string) => void;
  onExport: (kind: 'json' | 'csv') => void;
  onImport: (file: File) => void;
  themeMode: ThemeMode;
  onThemeChange: (mode: ThemeMode) => void;
};

export function WorkspaceView({
  active,
  state,
  partners,
  onAdd,
  onSelect,
  onNavigate,
  onExport,
  onImport,
  themeMode,
  onThemeChange,
}: Props) {
  const { updateMeta } = useStore();
  const [jurisdiction, setJurisdiction] = useState<'Dubai' | 'Saudi'>('Dubai');
  const [graphFilter, setGraphFilter] = useState('All');

  const tactical = state.tactical?.metrics ?? [];
  const layers = state.strategicNetwork?.layers ?? [];
  const ecosystem = state.ecosystem ?? { names: [] };
  const gov = state.governmentPathways ?? [];
  const infra = state.infrastructureSlots ?? [];
  const channels = state.capitalChannels ?? [];
  const decisions = state.decisions ?? [];
  const stack =
    jurisdiction === 'Dubai' ? state.regulatedStack?.dubai ?? [] : state.regulatedStack?.saudi ?? [];

  if (active === 'Tactical 45') {
    return (
      <section>
        <PageHeader
          kicker="13 AUG → 26 SEP 2026"
          title="Tactical 45"
          description="A small number of relationships must materially accelerate D001, revenue, Saudi validation, or runway."
          action={
            <button type="button" className="btn primary" onClick={onAdd}>
              + Add tactical partner
            </button>
          }
        />
        <div className="grid g4">
          <Card>
            <Stat label="Founder time" value={`${state.tactical?.founderTimePct ?? 11.5}%`} foot="inside 10–15% budget" />
          </Card>
          <Card>
            <Stat
              label="P4 / P5 evidence"
              value={partners.filter((p) => Number(p.evidence?.slice(1)) >= 4).length}
              foot="target ≥ 3"
            />
          </Card>
          <Card>
            <Stat label="Open blockers" value={4} foot="2 critical" />
          </Card>
          <Card>
            <Stat label="Closest to P5" value="1 gate" foot="regulated route" size="sm" />
          </Card>
        </div>

        <div className="section grid g-2-1">
          <div>
            <SectionHead title="Value matrix" hint="Tactical vs strategic" />
            <div className="value-matrix">
              <div className="matrix-axis vertical">STRATEGIC VALUE ↑</div>
              <div className="matrix-axis horizontal">45-DAY VALUE →</div>
              <div className="quadrant">
                <span className="dim tiny">CULTIVATE</span>
                <div className="dim tiny">High strategic · low tactical</div>
                <strong>Hedera</strong>
                <strong>Stellar</strong>
              </div>
              <div className="quadrant prioritize">
                <span className="dim tiny">PRIORITIZE NOW</span>
                <div className="dim tiny">High strategic · high tactical</div>
                <strong>Dubai Regulated Route A</strong>
                <strong>GCC Wealth Channel 01</strong>
              </div>
              <div className="quadrant">
                <span className="dim tiny">PARK</span>
                <div className="dim tiny">Low strategic · low tactical</div>
                <strong>Generic MOU targets</strong>
              </div>
              <div className="quadrant">
                <span className="dim tiny">USE TACTICALLY</span>
                <div className="dim tiny">Low strategic · high tactical</div>
                <strong>Custody Provider 01</strong>
              </div>
            </div>
          </div>
          <aside className="grid" style={{ gap: 14, alignContent: 'start' }}>
            <Card title="Day-15 gate">
              <p style={{ marginTop: 0 }}>Reduce breadth if proof is missing.</p>
              {(state.tactical?.day15Gates ?? []).map((g) => (
                <ChecklistRow key={g.id} ok={g.checked} label={g.label} />
              ))}
              <div className="mt8 dim tiny">
                {(state.tactical?.day15Gates ?? []).filter((g) => g.checked).length} of{' '}
                {(state.tactical?.day15Gates ?? []).length} signals secured
              </div>
            </Card>
            <Card title="Tactical question">
              <blockquote style={{ margin: 0, fontSize: 14.5, lineHeight: 1.45 }}>
                “What will this relationship materially change before Day 45?”
              </blockquote>
            </Card>
          </aside>
        </div>

        {tactical.length > 0 && (
          <div className="section grid g4">
            {tactical.map((item) => (
              <Card key={item.area}>
                <Stat label={item.area} value={item.metric} foot={item.detail} size="sm" />
              </Card>
            ))}
          </div>
        )}
      </section>
    );
  }

  if (active === 'Strategic Network') {
    return (
      <section>
        <PageHeader
          kicker="3–36 MONTH NETWORK"
          title="Strategic Network"
          description="The relationships Blocks needs to orchestrate 100 assets across four jurisdictions."
        />
        <div className="grid g3">
          {[
            ['H1', 'NOW', '0–90 days', 'Direct proof & revenue'],
            ['H2', 'BUILD', '3–12 months', 'Repeatable growth'],
            ['H3', 'MOAT', '12–36 months', 'Network effects & defensibility'],
          ].map((h) => (
            <Card key={h[0]}>
              <div className="row tight">
                <Chip tone="accent">{h[0]}</Chip>
                <Chip>{h[1]}</Chip>
              </div>
              <strong style={{ display: 'block', marginTop: 10 }}>{h[2]}</strong>
              <div className="dim tiny mt8">{h[3]}</div>
            </Card>
          ))}
        </div>
        <div className="section">
          <SectionHead
            title="Coverage by strategic layer"
            right={<Chip>{state.strategicNetwork?.health ?? 0} / 100 health</Chip>}
          />
          <div className="grid g4">
            {layers.map((layer) => (
              <Card
                key={layer.id}
                title={layer.label}
                right={
                  <Chip tone={layer.state === 'Weak' ? 'amber' : 'green'}>{layer.state}</Chip>
                }
              >
                <div className="dim tiny" style={{ marginBottom: 8 }}>
                  {layer.id}
                </div>
                <p className="dim" style={{ marginTop: 0, fontSize: 13 }}>
                  {layer.thesis}
                </p>
                <BarRow label="Coverage" actual={layer.value} suffix="" />
                <button
                  type="button"
                  className="btn sm block"
                  onClick={() =>
                    onNavigate(
                      layer.label.includes('Regulated')
                        ? 'Regulated Stack'
                        : layer.label.includes('Distribution')
                          ? 'Capital & Distribution'
                          : 'Partner Pipeline',
                    )
                  }
                >
                  Open layer
                </button>
              </Card>
            ))}
          </div>
        </div>
        {state.strategicNetwork?.concentrationRisk && (
          <div className="section">
            <Banner tone="warn">
              <strong>Concentration risk.</strong> {state.strategicNetwork.concentrationRisk}{' '}
              <button type="button" className="btn sm" onClick={() => onNavigate('Decisions')}>
                Review decision
              </button>
            </Banner>
          </div>
        )}
      </section>
    );
  }

  if (active === 'Partner Pipeline') {
    const columns = [
      ['IDENTIFIED', partners.filter((p) => p.stage.toLowerCase() === 'identified')],
      ['DISCOVERY', partners.filter((p) => p.stage.toLowerCase() === 'discovery')],
      ['FIT CONFIRMED', partners.filter((p) => p.stage.toLowerCase() === 'fit confirmed')],
      ['PILOT READY', partners.filter((p) => p.stage.toLowerCase() === 'pilot ready')],
    ] as const;
    return (
      <section>
        <PageHeader
          kicker="EVIDENCE-GATED CRM"
          title="Partner Pipeline"
          description="Relationships advance only when exit evidence exists—activity alone earns no credit."
          action={
            <button type="button" className="btn primary" onClick={onAdd}>
              + Add partner
            </button>
          }
        />
        <div className="row" style={{ marginBottom: 14 }}>
          <Chip>
            <b>{partners.length}</b> tracked
          </Chip>
          <Chip tone="accent">
            <b>{partners.filter((p) => Number(p.evidence?.slice(1)) >= 3).length}</b> leverage signals
          </Chip>
          <Chip tone="amber">
            <b>{partners.filter((p) => p.evidence === 'P0').length}</b> hypotheses
          </Chip>
        </div>
        <div className="kanban">
          {columns.map(([stage, items]) => (
            <Card key={stage} title={stage} right={<Chip>{items.length}</Chip>} flush>
              <div className="kanban-col-body">
                {items.map((p) => (
                  <button
                    type="button"
                    className="kanban-card"
                    key={p.id || p.name}
                    onClick={() => onSelect(p)}
                  >
                    <div className="row tight">
                      <span className="dim tiny">{p.type}</span>
                      <div className="spacer" />
                      <StagePill value={p.evidence} />
                    </div>
                    <strong>{p.name}</strong>
                    <p className="dim tiny" style={{ margin: '6px 0 0' }}>
                      {p.next}
                    </p>
                    <div className="row tight mt8">
                      <span className="num tiny">Score {p.score}</span>
                      <div className="spacer" />
                      <time className="dim tiny">{p.due}</time>
                    </div>
                  </button>
                ))}
                {items.length === 0 && <Empty title="Empty stage">No partner has earned this stage.</Empty>}
              </div>
            </Card>
          ))}
        </div>
      </section>
    );
  }

  if (active === 'Ecosystem Anchors') {
    const names = ecosystem.names?.length
      ? ecosystem.names
      : ['Hedera', 'Avalanche', 'Stellar', 'Polygon', 'Chainlink'];
    return (
      <section>
        <PageHeader
          kicker="CHAIN-AGNOSTIC BY DEFAULT"
          title="Ecosystem Anchors"
          description="Select zero or one primary anchor only when a concrete support package creates leverage."
        />
        <Banner>
          <strong>Chain-agnostic doctrine.</strong>{' '}
          {ecosystem.doctrine || 'Blocks is the product. The chain is infrastructure.'} Any exclusivity
          request triggers a founder decision.
        </Banner>
        <div className="section grid g3">
          {names.map((name, i) => (
            <Card
              key={name}
              title={name}
              right={<span className="mid-num">{ecosystem.scores?.[i] ?? 50}</span>}
            >
              <div className="dim tiny" style={{ marginBottom: 10 }}>
                IDENTIFIED · <StagePill value="P0" />
              </div>
              <BarRow label="Commercial" actual={ecosystem.commercial?.[i] ?? 40} />
              <BarRow label="Funding" actual={ecosystem.funding?.[i] ?? 40} />
              <BarRow label="Technical" actual={ecosystem.technical?.[i] ?? 40} />
              <p className="dim" style={{ fontSize: 13 }}>
                {ecosystem.asks?.[i] || 'Define the ask'}
              </p>
              <button
                type="button"
                className="btn sm block"
                onClick={() => onSelect(partners.find((p) => p.name === name) || partners[0])}
              >
                Open candidate
              </button>
            </Card>
          ))}
        </div>
      </section>
    );
  }

  if (active === 'Regulated Stack') {
    return (
      <section>
        <PageHeader
          kicker="JURISDICTION ARCHITECTURE"
          title="Regulated Stack"
          description="Every permission and capability needed to make a real transaction executable."
        />
        <div className="row" style={{ marginBottom: 14 }}>
          <Segmented
            value={jurisdiction}
            onChange={(v) => setJurisdiction(v as 'Dubai' | 'Saudi')}
            options={['Dubai', 'Saudi']}
          />
          <button type="button" className="btn sm" disabled>
            Qatar · Parked
          </button>
          <button type="button" className="btn sm" disabled>
            Bahrain · Parked
          </button>
        </div>
        <Card flush title={`${jurisdiction} stack`}>
          <div className="table-wrap">
            <table className="table">
              <thead>
                <tr>
                  <th>#</th>
                  <th>Capability</th>
                  <th>Preferred partner</th>
                  <th>Status</th>
                  <th>Evidence</th>
                  <th>Blocker</th>
                </tr>
              </thead>
              <tbody>
                {stack.map((item, i) => (
                  <tr key={item.name}>
                    <td className="num">{String(i + 1).padStart(2, '0')}</td>
                    <td>
                      <strong>{item.name}</strong>
                      <div className="dim tiny">
                        {item.requiredForD001 ? 'Required for D001' : 'Transaction stack'}
                      </div>
                    </td>
                    <td>{item.preferredPartner}</td>
                    <td>
                      <Chip tone={item.status === 'ARCHITECTURE' ? 'green' : 'amber'}>{item.status}</Chip>
                    </td>
                    <td>
                      <StagePill value={item.evidence} />
                    </td>
                    <td className="dim">{item.blocker}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      </section>
    );
  }

  if (active === 'Capital & Distribution') {
    const rows = channels.length
      ? channels
      : Array.from({ length: 10 }, (_, i) => ({
          id: `slot-${i}`,
          name: i === 0 ? 'GCC Wealth Channel 01' : `Target slot ${String(i + 1).padStart(2, '0')}`,
          geography: i === 0 ? 'GCC' : '—',
          investorType: i === 0 ? 'Wealth manager' : '—',
          ticket: i === 0 ? '$250k–1m' : '—',
          d001Relevance: i === 0 ? 'High' : 'Unscored',
          evidence: i === 0 ? 'P3' : 'P0',
          next: i === 0 ? 'Share acceptance memo' : 'Qualify target',
        }));
    return (
      <section>
        <PageHeader
          kicker="DISTRIBUTION SIGNALS"
          title="Capital & Distribution"
          description="Interest in tokenization is not capital evidence; require a specific asset and structure they would consider."
          action={
            <button type="button" className="btn primary" onClick={onAdd}>
              + Add channel
            </button>
          }
        />
        <Card flush>
          <div className="table-wrap">
            <table className="table">
              <thead>
                <tr>
                  <th>Target</th>
                  <th>Geography</th>
                  <th>Investor type</th>
                  <th>Ticket</th>
                  <th>D001 relevance</th>
                  <th>Evidence</th>
                  <th>Next action</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((row, i) => (
                  <tr
                    key={row.id || row.name}
                    style={{ cursor: 'pointer' }}
                    onClick={i === 0 ? () => onSelect(partners[1] || partners[0]) : onAdd}
                  >
                    <td>
                      <strong>{row.name}</strong>
                    </td>
                    <td>{row.geography || '—'}</td>
                    <td>{row.investorType || '—'}</td>
                    <td>{row.ticket || '—'}</td>
                    <td>{row.d001Relevance || 'Unscored'}</td>
                    <td>
                      <StagePill value={row.evidence || 'P0'} />
                    </td>
                    <td>{row.next || 'Qualify target'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      </section>
    );
  }

  if (active === 'Government') {
    return (
      <section>
        <PageHeader
          kicker="INSTITUTIONAL PATHWAYS"
          title="Government & Institutions"
          description="Use Institutional Relationship unless an organization has formally agreed to partner."
        />
        <div className="grid g3">
          {gov.map((g) => (
            <Card
              key={g.id || g.name}
              title={g.name}
              right={<Chip tone={g.status === 'Parked' ? 'flat' : 'accent'}>{g.status}</Chip>}
            >
              <div className="dim tiny" style={{ marginBottom: 8 }}>
                {g.jurisdiction}
              </div>
              <p style={{ marginTop: 0 }}>{g.focus}</p>
              <Stat label="Formal status" value={g.formalStatus} size="sm" />
              <div className="mt8">
                <StagePill value={g.evidence || 'P0'} />
              </div>
            </Card>
          ))}
        </div>
      </section>
    );
  }

  if (active === 'Infrastructure') {
    return (
      <section>
        <PageHeader
          kicker="TRANSACTION STACK"
          title="Infrastructure"
          description="No critical D001 activity should exist without a responsible provider and backup."
        />
        <div className="grid g3">
          {infra.map((slot) => (
            <Card
              key={slot.id || slot.name}
              title={slot.name}
              right={
                <Chip tone={slot.requiredForD001 ? 'red' : ''}>
                  {slot.requiredForD001 ? 'D001 REQUIRED' : 'LONG-TERM'}
                </Chip>
              }
            >
              <div className="num dim tiny" style={{ marginBottom: 8 }}>
                {String(slot.slotIndex ?? 0).padStart(2, '0')}
              </div>
              <p style={{ marginTop: 0 }}>{slot.provider || 'No provider assigned'}</p>
              <div className="row tight">
                <Chip tone={slot.coverage === 'COVERED' ? 'green' : 'amber'}>{slot.coverage}</Chip>
                <StagePill value={slot.evidence || 'P0'} />
              </div>
            </Card>
          ))}
        </div>
      </section>
    );
  }

  if (active === 'Partner Graph') {
    return (
      <section>
        <PageHeader
          kicker="STRUCTURAL VIEW"
          title="Partner Graph"
          description="See where Blocks has leverage, where it is structurally weak, and where one relationship carries too much dependency."
        />
        <div className="row" style={{ marginBottom: 14 }}>
          <Segmented
            value={graphFilter}
            onChange={setGraphFilter}
            options={['All', 'Tactical', 'Strategic', 'Dubai', 'Saudi', 'P4+']}
          />
        </div>
        <Card className="network-graph-card">
          <div className="network-graph">
            <svg viewBox="0 0 1000 520" preserveAspectRatio="none" aria-hidden="true">
              <line x1="500" y1="260" x2="180" y2="100" />
              <line x1="500" y1="260" x2="500" y2="70" />
              <line x1="500" y1="260" x2="820" y2="110" />
              <line x1="500" y1="260" x2="850" y2="330" />
              <line x1="500" y1="260" x2="620" y2="455" />
              <line x1="500" y1="260" x2="300" y2="450" />
              <line x1="500" y1="260" x2="150" y2="325" />
            </svg>
            <button type="button" className="graph-node center">
              BLOCKS<small>ORCHESTRATOR</small>
            </button>
            <button type="button" className="graph-node n1">
              ASSETS<small>Developing</small>
            </button>
            <button type="button" className="graph-node n2">
              REGULATION<small>1 P4 route</small>
            </button>
            <button type="button" className="graph-node n3">
              CAPITAL<small>Weak</small>
            </button>
            <button type="button" className="graph-node n4">
              INFRASTRUCTURE<small>4 gaps</small>
            </button>
            <button type="button" className="graph-node n5">
              BLOCKCHAIN<small>5 hypotheses</small>
            </button>
            <button type="button" className="graph-node n6">
              GOVERNMENT<small>Developing</small>
            </button>
            <button type="button" className="graph-node n7">
              DISTRIBUTION<small>1 P3 signal</small>
            </button>
          </div>
        </Card>
      </section>
    );
  }

  if (active === 'Evidence') {
    return (
      <section>
        <PageHeader
          kicker="PROOF, NOT ACTIVITY"
          title="Partnership Evidence"
          description="A meeting, logo, generic MOU, or ‘explore synergies’ conversation is not leverage."
        />
        <div className="ladder">
          {[...PARTNERSHIP_EVIDENCE].reverse().map((x) => {
            const count = partners.filter((p) => p.evidence === x.id).length;
            return (
              <div key={x.id} className={`rung ${x.id === 'P5' ? 'e5' : x.id === 'P4' ? 'e4' : ''}`}>
                <div className="id">{x.id}</div>
                <div>
                  <div className="nm">{x.name}</div>
                  <div className="ln">
                    {x.note} — {x.line}
                  </div>
                </div>
                <div className={`ct ${count === 0 ? 'zero' : ''}`}>{count}</div>
              </div>
            );
          })}
        </div>
        <div className="section">
          <Banner tone="ok">
            <strong>Stage movement rule.</strong> No partner advances without exit evidence.{' '}
            <button type="button" className="btn sm" onClick={() => onNavigate('Meetings')}>
              Log evidence
            </button>
          </Banner>
        </div>
      </section>
    );
  }

  if (active === 'Meetings') {
    const top = partners[0];
    return (
      <section>
        <PageHeader
          kicker="UNDER TWO MINUTES"
          title="Meeting Workflow"
          description="Enter every conversation with one outcome; leave with explicit offers, asks, owners, and dates."
          action={
            <button type="button" className="btn primary">
              + Log meeting
            </button>
          }
        />
        <div className="grid g2">
          <Card title="Next meeting" sub={top?.name || 'No partner selected'}>
            <div className="dim tiny" style={{ letterSpacing: '0.08em', textTransform: 'uppercase' }}>
              One objective
            </div>
            <strong style={{ display: 'block', marginTop: 6, fontSize: 16 }}>
              {top?.next || 'Define the meeting objective.'}
            </strong>
          </Card>
          <Card title="60-second closeout" sub="After the meeting">
            {['What did they explicitly offer?', 'What did they ask from Blocks?', 'What commitment exists?'].map(
              (x, i) => (
                <label key={x} className="field">
                  <span>
                    {String(i + 1).padStart(2, '0')} · Question
                  </span>
                  <input placeholder={x} />
                </label>
              ),
            )}
          </Card>
        </div>
      </section>
    );
  }

  if (active === 'Decisions') {
    return (
      <section>
        <PageHeader
          kicker="FOUNDER DECISION LOG"
          title="Decisions"
          description="Make trade-offs explicit when exclusivity, economics, regulation, or strategic optionality is at stake."
          action={
            <button type="button" className="btn primary">
              + New decision
            </button>
          }
        />
        <div className="grid" style={{ gap: 10 }}>
          {decisions.map((d) => (
            <Card
              key={d.id || d.question}
              title={d.question}
              right={<Chip tone={d.state === 'Open' ? 'amber' : 'green'}>{d.state}</Chip>}
            >
              <p className="dim" style={{ marginTop: 0 }}>
                {d.note}
              </p>
              <div className="grid g2">
                <Stat label="Tactical" value={d.tacticalImpact ?? '—'} size="sm" />
                <Stat label="Strategic" value={d.strategicImpact ?? '—'} size="sm" />
              </div>
              <div className="dim tiny mt8">{d.due}</div>
            </Card>
          ))}
          {!decisions.length && <Empty title="No decisions">Log a founder trade-off when needed.</Empty>}
        </div>
      </section>
    );
  }

  if (active === 'Weekly Review') {
    return (
      <section>
        <PageHeader
          kicker="FRIDAY · WEEK 1"
          title="Weekly Tactical Review"
          description="Concentrate on the relationships that changed what Blocks can do—and park the ones that did not."
          action={
            <button type="button" className="btn primary">
              Complete review
            </button>
          }
        />
        <div className="grid g2">
          {[
            ['Evidence gained', 'What reached P4 or P5?'],
            ['D001 unlocked', 'Which capability moved?'],
            ['Revenue unlocked', 'What commercial value appeared?'],
            ['Saudi unlocked', 'Which pathway became concrete?'],
          ].map((q, i) => (
            <Card key={q[0]} title={`${String(i + 1).padStart(2, '0')} · ${q[0]}`} sub={q[1]}>
              <textarea placeholder="Capture evidence, not impressions…" rows={4} />
            </Card>
          ))}
        </div>
      </section>
    );
  }

  return (
    <section>
      <PageHeader
        kicker="WORKSPACE CONTROL"
        title="Settings & Data"
        description="Your profile, organization details, members, and workspace import/export."
      />
      <SettingsAccountPanels
        themeMode={themeMode}
        onThemeChange={onThemeChange}
        sprintStart={state.meta?.sprintStart || DEFAULT_SPRINT_START}
        sprintEnd={state.meta?.sprintEnd}
        totalDays={state.meta?.totalDays || DEFAULT_TOTAL_DAYS}
        onSaveSprint={async (start) => {
          const totalDays = state.meta?.totalDays || DEFAULT_TOTAL_DAYS;
          await updateMeta({
            sprintStart: start,
            sprintEnd: sprintEndFromStart(start, totalDays),
            totalDays,
          });
        }}
      />
      <div className="section">
        <MembersPanel />
      </div>
      <div className="section grid g2">
        <Card title="Export workspace">
          <p className="dim">Create a portable snapshot of partners, scores, evidence, and next actions.</p>
          <div className="row">
            <button type="button" className="btn" onClick={() => onExport('json')}>
              Export JSON
            </button>
            <button type="button" className="btn" onClick={() => onExport('csv')}>
              Export CSV
            </button>
          </div>
        </Card>
        <Card title="Import snapshot">
          <p className="dim">Import a JSON workspace snapshot through the API.</p>
          <label className="btn" style={{ display: 'inline-flex', cursor: 'pointer' }}>
            Choose JSON
            <input
              type="file"
              accept=".json,application/json"
              hidden
              onChange={(e) => e.target.files?.[0] && onImport(e.target.files[0])}
            />
          </label>
        </Card>
      </div>
    </section>
  );
}

function copyText(value: string) {
  if (!value || !navigator.clipboard?.writeText) return;
  void navigator.clipboard.writeText(value).catch(() => {});
}

function MetaRow({
  label,
  value,
  mono = true,
  copyable = false,
}: {
  label: string;
  value?: string | null;
  mono?: boolean;
  copyable?: boolean;
}) {
  if (value == null || value === '') return null;
  return (
    <div className="profile-meta-row">
      <span className="label">{label}</span>
      <span className={`value ${mono ? '' : 'plain'}`}>
        {value}
        {copyable && (
          <button type="button" className="btn ghost sm" onClick={() => copyText(value)}>
            Copy
          </button>
        )}
      </span>
    </div>
  );
}

function SettingsAccountPanels({
  themeMode,
  onThemeChange,
  sprintStart,
  sprintEnd,
  totalDays,
  onSaveSprint,
}: {
  themeMode: ThemeMode;
  onThemeChange: (mode: ThemeMode) => void;
  sprintStart: string;
  sprintEnd?: string;
  totalDays: number;
  onSaveSprint: (startIso: string) => Promise<void>;
}) {
  const [me, setMe] = useState<ProfileMe | null>(null);
  const [loading, setLoading] = useState(true);
  const [day0, setDay0] = useState(sprintStart);
  const [sprintMsg, setSprintMsg] = useState('');
  const [sprintBusy, setSprintBusy] = useState(false);

  useEffect(() => {
    setDay0(sprintStart);
  }, [sprintStart]);

  useEffect(() => {
    let cancelled = false;
    void api
      .getMe()
      .then((data) => {
        if (!cancelled) setMe(data);
      })
      .catch(() => {
        if (!cancelled) setMe(null);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const previewEnd = sprintEndFromStart(day0, totalDays);
  const previewDay = sprintDayIndex(day0, new Date(), totalDays);
  const until = daysUntilStart(day0);

  async function saveSprint(e: FormEvent) {
    e.preventDefault();
    setSprintMsg('');
    setSprintBusy(true);
    try {
      await onSaveSprint(day0);
      setSprintMsg('Sprint Day 0 saved.');
    } catch (err) {
      setSprintMsg(err instanceof Error ? err.message : 'Save failed');
    } finally {
      setSprintBusy(false);
    }
  }

  return (
    <div className="grid g2">
      <ProfilePanel me={me} loading={loading} onUpdated={setMe} />
      <div className="grid" style={{ gap: 14, alignContent: 'start' }}>
        <OrganizationCard me={me} loading={loading} />
        <Card title="Sprint Day 0" sub="When the 45-day clock begins">
          <p className="dim" style={{ marginTop: 0 }}>
            Day 0 is the start date. Day 1 is the next calendar day. End date is computed automatically.
          </p>
          {sprintMsg && (
            <Banner tone={sprintMsg.includes('saved') ? 'ok' : 'stop'}>{sprintMsg}</Banner>
          )}
          <form onSubmit={saveSprint}>
            <label className="field">
              Day 0 date
              <input
                type="date"
                value={day0}
                onChange={(e) => setDay0(e.target.value)}
                required
              />
            </label>
            <div className="row tight" style={{ marginBottom: 12 }}>
              <button
                type="button"
                className="btn sm"
                onClick={() => setDay0(nextMondayIso())}
              >
                Next Monday
              </button>
              <span className="dim tiny">
                {until > 0
                  ? `Starts in ${until} day${until === 1 ? '' : 's'} · currently Day ${previewDay}`
                  : `Today is Day ${previewDay} of ${totalDays}`}
              </span>
            </div>
            <div className="profile-meta" style={{ marginBottom: 12 }}>
              <MetaRow label="Day 0" value={formatSprintLabel(day0)} mono={false} />
              <MetaRow label="Ends" value={formatSprintLabel(previewEnd)} mono={false} />
              <MetaRow
                label="Stored end"
                value={sprintEnd ? formatSprintLabel(sprintEnd) : '—'}
                mono={false}
              />
            </div>
            <button type="submit" className="btn primary" disabled={sprintBusy || !day0}>
              {sprintBusy ? 'Saving…' : 'Save Day 0'}
            </button>
          </form>
        </Card>
        <Card title="Appearance" sub="Signed-in theme preference">
          <p className="dim" style={{ marginTop: 0 }}>
            Auto follows your operating system. Public pages always use system appearance.
          </p>
          <ThemeSwitch value={themeMode} onChange={onThemeChange} />
        </Card>
      </div>
    </div>
  );
}

function OrganizationCard({ me, loading }: { me: ProfileMe | null; loading: boolean }) {
  const org = me?.organization;
  return (
    <Card title="Organization" sub="Workspace identity">
      {loading && !me ? (
        <Empty>Loading…</Empty>
      ) : (
        <div className="profile-meta">
          <MetaRow label="Name" value={org?.name} mono={false} />
          <MetaRow label="Organization ID" value={org?.id || me?.orgId} copyable />
          <MetaRow label="Your role" value={me?.memberRole} mono={false} />
          <MetaRow label="Created" value={org?.createdAt} />
          <MetaRow label="Updated" value={org?.updatedAt} />
        </div>
      )}
    </Card>
  );
}

function ProfilePanel({
  me,
  loading,
  onUpdated,
}: {
  me: ProfileMe | null;
  loading: boolean;
  onUpdated: (next: ProfileMe) => void;
}) {
  const fileRef = useRef<HTMLInputElement>(null);
  const [displayName, setDisplayName] = useState('');
  const [msg, setMsg] = useState('');
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (me?.displayName) setDisplayName(me.displayName);
  }, [me?.displayName]);

  async function saveName(e: FormEvent) {
    e.preventDefault();
    setMsg('');
    setBusy(true);
    try {
      const next = await api.updateMe({ displayName });
      patchSession({
        displayName: next.displayName,
        profilePictureUrl: next.profilePictureUrl,
        organization: next.organization,
        memberRole: next.memberRole,
      });
      onUpdated(next);
      setMsg('Display name saved.');
    } catch (err) {
      setMsg(err instanceof Error ? err.message : 'Save failed');
    } finally {
      setBusy(false);
    }
  }

  async function onPickFile(file: File | undefined) {
    if (!file) return;
    setMsg('');
    setBusy(true);
    try {
      const type = file.type || 'image/jpeg';
      const next = await api.uploadProfilePicture(file, type);
      patchSession({
        displayName: next.displayName,
        profilePictureUrl: next.profilePictureUrl,
        organization: next.organization,
        memberRole: next.memberRole,
      });
      onUpdated(next);
      setMsg('Profile picture updated.');
    } catch (err) {
      setMsg(err instanceof Error ? err.message : 'Upload failed');
    } finally {
      setBusy(false);
      if (fileRef.current) fileRef.current.value = '';
    }
  }

  return (
    <Card title="Your profile" sub="Avatar and account">
      {msg && (
        <Banner tone={msg.includes('saved') || msg.includes('updated') ? 'ok' : 'stop'}>{msg}</Banner>
      )}
      {loading && !me ? (
        <Empty>Loading…</Empty>
      ) : (
        <>
          <div className="profile-avatar-row">
            <UserAvatar url={me?.profilePictureUrl} name={me?.displayName || me?.email || '?'} size={72} />
            <div>
              <input
                ref={fileRef}
                type="file"
                accept="image/jpeg,image/png,image/webp"
                hidden
                onChange={(e) => void onPickFile(e.target.files?.[0])}
              />
              <button
                type="button"
                className="btn primary sm"
                disabled={busy}
                onClick={() => fileRef.current?.click()}
              >
                Upload photo
              </button>
              <div className="dim tiny mt8">JPEG, PNG, or WebP · max 5 MB</div>
            </div>
          </div>
          <form className="mt14" onSubmit={saveName}>
            <label className="field">
              Display name
              <input value={displayName} onChange={(e) => setDisplayName(e.target.value)} />
            </label>
            <button type="submit" className="btn primary" disabled={busy || !displayName.trim()}>
              Save name
            </button>
          </form>
          <div className="profile-meta mt14">
            <MetaRow label="Email" value={me?.email} mono={false} />
            <MetaRow label="User ID" value={me?.userId} copyable />
            <MetaRow label="Role" value={me?.memberRole} mono={false} />
            <MetaRow label="Status" value={me?.status} mono={false} />
          </div>
        </>
      )}
    </Card>
  );
}

function MembersPanel() {
  const session = loadSession();
  const [members, setMembers] = useState<OrgMember[]>([]);
  const [msg, setMsg] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [role, setRole] = useState<MemberRole>('member');
  const canInvite = session?.memberRole === 'owner' || session?.memberRole === 'admin';

  async function reload() {
    try {
      const data = await api.listMembers();
      setMembers(data.members ?? []);
    } catch (err) {
      setMsg(err instanceof Error ? err.message : 'Failed to load members');
    }
  }

  useEffect(() => {
    void reload();
  }, []);

  async function invite(e: FormEvent) {
    e.preventDefault();
    setMsg('');
    try {
      await api.inviteMember({ email, password, displayName, role });
      setEmail('');
      setPassword('');
      setDisplayName('');
      setMsg('Member invited.');
      await reload();
    } catch (err) {
      setMsg(err instanceof Error ? err.message : 'Invite failed');
    }
  }

  return (
    <Card
      title={session?.organization?.name || session?.orgId || 'Members'}
      sub="Roles: owner, admin, member, viewer. Invites require owner or admin."
    >
      {msg && <Banner tone={msg.includes('invited') ? 'ok' : 'stop'}>{msg}</Banner>}
      <ul className="members-list">
        {members.map((m) => (
          <li key={m.id}>
            <div>
              <strong>{m.displayName || m.email}</strong>
              <div className="dim tiny">{m.email}</div>
            </div>
            <Chip>{m.role}</Chip>
          </li>
        ))}
        {!members.length && <li className="dim">No members loaded.</li>}
      </ul>
      {canInvite && (
        <form className="members-invite mt14" onSubmit={invite}>
          <label className="field">
            Invite email
            <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} required />
          </label>
          <label className="field">
            Display name
            <input value={displayName} onChange={(e) => setDisplayName(e.target.value)} />
          </label>
          <label className="field">
            Temporary password
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              minLength={8}
            />
          </label>
          <label className="field">
            Role
            <select value={role} onChange={(e) => setRole(e.target.value as MemberRole)}>
              <option value="admin">Admin</option>
              <option value="member">Member</option>
              <option value="viewer">Viewer</option>
            </select>
          </label>
          <button type="submit" className="btn primary">
            Invite member
          </button>
        </form>
      )}
    </Card>
  );
}

export function useSprintDay(start = DEFAULT_SPRINT_START, totalDays = DEFAULT_TOTAL_DAYS): number {
  return useMemo(() => sprintDayIndex(start, new Date(), totalDays), [start, totalDays]);
}
